"use client";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/pro-solid-svg-icons/faTimes";
import { faBars } from "@fortawesome/pro-solid-svg-icons/faBars";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import CN from "classnames";
import { useSearchParams } from "next/navigation";
import { forwardRef, useId, useState } from "react";
import { styled } from "@mui/material/styles";
import { LAYOUT_MOBILE_DESKTOP_RESPONSIVE_BREAKPOINT } from "@portal/app/_components/ui-elements/Layout";
import { usePageContext } from "../../context/PageContext";
import { VocationSearchAutocomplete } from "@portal/app/_components/ui-elements/VocationSearchAutocomplete";
import { useDimensions } from "@portal/app/_components/hooks/useDimensions";
import { ProfileButton } from "./components/ProfileButton";
import { PrimaryActionButtons } from "./components/PrimaryActionButtons";
import { NavLogo } from "@portal/app/_components/ui-elements/CustomerNavBar/components/NavLogo";
import { ExperimentsLab } from "./components/ExperimentsLab";

type CustomerNavBarProps = {
  // If True, the background color is removed
  withTransparentBackground?: boolean;
  // If True, then var(--vh) padding is used to horizontally align the navbar
  // contents to a page that is also using var(--vh) margin/padding
  withHorizontalConstraint?: boolean;
  // If True, the logo is darkened, and the text button color is inverted.
  // this is to support full bleed on pages with a dark top section
  isOnDarkBackground?: boolean;
  shouldHideSearch?: boolean;
};
export const CustomerNavBar = forwardRef<HTMLDivElement, CustomerNavBarProps>(
  (
    {
      withTransparentBackground = false,
      withHorizontalConstraint = false,
      isOnDarkBackground = false,
      shouldHideSearch = false,
    }: CustomerNavBarProps,
    ref
  ) => {
    const { isMobile } = usePageContext();
    const searchParams = useSearchParams();
    const zip = searchParams?.get("zip") as string | undefined;
    const [middleContainerRef, middleContainerDimensions] = useDimensions();
    const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);
    const mobileMenuId = useId();
    const [isExperimentsLabOpen, setExperimentsLabOpen] = useState(false);

    /*
  In desktop mode, everything in `NavHolster` is displayed normally in line with
  other nav items. In mobile mode, `NavHolster` becomes the mobile menu and its
  display is managed in state and toggled via the Menu button. This
  implementation avoids SSR issues.
   */

    const tooSmallForSearchBar = !!(
      middleContainerDimensions && middleContainerDimensions.width < 350
    );

    return (
      <NavContainer
        className={CN("dream_search_nav_bar", {
          withTransparentBackground,
          withHorizontalConstraint,
          isOnDarkBackground: isOnDarkBackground && !isMobileMenuOpen,
          ...(isMobileMenuOpen && { mobileOpen: true }),
        })}
        $shouldHideSearch={shouldHideSearch}
        ref={ref}
      >
        {isMobile && (
          <MobileTopNavSection>
            <NavMobileMenuButtonWrapper
              $isOnDarkBackground={isOnDarkBackground && !isMobileMenuOpen}
            >
              <NavMobileMenuButton
                onClick={() => {
                  setMobileMenuOpen?.((prev) => !prev);
                }}
                data-testid="nav-menu"
                aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
                aria-expanded={isMobileMenuOpen}
                aria-controls={isMobileMenuOpen ? mobileMenuId : undefined}
              >
                {isMobileMenuOpen ? (
                  <FontAwesomeIcon icon={faTimes} size="xl" />
                ) : (
                  <FontAwesomeIcon icon={faBars} size="xl" />
                )}
              </NavMobileMenuButton>
            </NavMobileMenuButtonWrapper>
            <NavLogo isMobileMenuOpen={isMobileMenuOpen} isOnDarkBackground={isOnDarkBackground} />
            <ProfileButton
              isMobileMenuOpen={isMobileMenuOpen}
              setMobileMenuOpen={setMobileMenuOpen}
              onOpenExperimentsLab={() => setExperimentsLabOpen(true)}
            />
          </MobileTopNavSection>
        )}
        {isMobile && !shouldHideSearch && (
          <SearchContainer>
            <VocationSearchAutocomplete zip={zip} trackingEvent="nav_mobile_search" />
          </SearchContainer>
        )}
        {(!isMobile || isMobileMenuOpen) && (
          <NavHolster
            id={mobileMenuId}
            $isMobileMenuOpen={isMobileMenuOpen}
            $shouldHideSearch={shouldHideSearch}
          >
            <LeftSideDesktopContainer>
              {!isMobile && (
                <NavLogo
                  isMobileMenuOpen={isMobileMenuOpen}
                  isOnDarkBackground={isOnDarkBackground}
                />
              )}
              <PrimaryActionButtons />
            </LeftSideDesktopContainer>
            {!isMobile && !shouldHideSearch && (
              <MiddleDesktopContainer
                ref={middleContainerRef}
                $tooSmallForSearchBar={tooSmallForSearchBar}
              >
                {!tooSmallForSearchBar && (
                  <SearchContainer>
                    <VocationSearchAutocomplete zip={zip} trackingEvent="nav_desktop_search" />
                  </SearchContainer>
                )}
              </MiddleDesktopContainer>
            )}
            <RightSideDesktopContainer>
              <UserContainer>
                {!isMobile && (
                  <ProfileButton
                    isMobileMenuOpen={isMobileMenuOpen}
                    setMobileMenuOpen={setMobileMenuOpen}
                    onOpenExperimentsLab={() => setExperimentsLabOpen(true)}
                  />
                )}
              </UserContainer>
            </RightSideDesktopContainer>
          </NavHolster>
        )}
        <ExperimentsLab
          open={isExperimentsLabOpen}
          onClose={() => {
            setMobileMenuOpen(false);
            setExperimentsLabOpen(false);
          }}
        />
      </NavContainer>
    );
  }
);

export const NavContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "$shouldHideSearch",
})<{
  $shouldHideSearch: boolean;
}>`
  position: relative;
  overflow: hidden;
  z-index: 999;
  height: var(--NavBarHeight);
  display: flex;
  align-items: center;
  background-color: ${({ theme }) => theme.palette.dream.grayLight};

  ${({ theme }) => theme.breakpoints.down(LAYOUT_MOBILE_DESKTOP_RESPONSIVE_BREAKPOINT)} {
    height: ${({ $shouldHideSearch }) => !$shouldHideSearch && "calc(var(--NavBarHeight) * 2)"};
    padding: ${({ $shouldHideSearch }) => ($shouldHideSearch ? "0 1rem 0 1rem" : "0.5rem 1rem")};
    justify-content: space-between;
    flex-wrap: wrap;
    position: sticky;
    top: 0;
  }

  ${({ theme }) => theme.breakpoints.up(LAYOUT_MOBILE_DESKTOP_RESPONSIVE_BREAKPOINT)} {
    background: ${({ theme }) => theme.palette.dream.grayLight}
      linear-gradient(to top, rgba(0, 0, 0, 0.02), rgba(0, 0, 0, 0)) repeat-x bottom;
    background-size: 100% 1rem;
    transition: 100ms linear all;
    padding: 0.5rem 2rem;
  }

  &.withTransparentBackground {
    // This is equivalent to drawing a 1px border on the bottom

    &:not(.mobileOpen) {
      background: transparent linear-gradient(to top, rgba(0, 0, 0, 0.05) 1px, rgba(0, 0, 0, 0) 1px)
        repeat-x bottom;
      backdrop-filter: blur(1rem);
      -webkit-backdrop-filter: blur(1rem);
    }
  }

  &.withHorizontalConstraint {
    padding-left: var(--vh);
    padding-right: var(--vh);
  }

  &.isOnDarkBackground {
    .nav-button {
      color: white;
    }
  }

  &.mobileOpen {
    &.isOnDarkBackground {
      background-color: ${({ theme }) => theme.palette.dream.grayMediumDark};
    }
  }
`;

const MobileTopNavSection = styled(Box)`
  ${({ theme }) => theme.breakpoints.down(LAYOUT_MOBILE_DESKTOP_RESPONSIVE_BREAKPOINT)} {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex: 1 0 50%;
    width: 100%;
    margin: 0 -1rem;
    padding: 0 1rem;
  }
`;

const LeftSideDesktopContainer = styled(Box)`
  ${({ theme }) => theme.breakpoints.up(LAYOUT_MOBILE_DESKTOP_RESPONSIVE_BREAKPOINT)} {
    display: flex;
    flex: 1 1 50%;
    align-items: center;

    justify-content: start;
  }
`;
const MiddleDesktopContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "$tooSmallForSearchBar",
})<{ $tooSmallForSearchBar: boolean }>`
  padding: 0 1rem;
  ${({ theme }) => theme.breakpoints.up("lg")} {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 1 1 50%;
  }
`;
const RightSideDesktopContainer = styled(Box)`
  ${({ theme }) => theme.breakpoints.up(LAYOUT_MOBILE_DESKTOP_RESPONSIVE_BREAKPOINT)} {
    display: flex;
    align-items: center;

    justify-content: end;
  }
`;

const NavMobileMenuButtonWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== "$isOnDarkBackground",
})<{
  $isOnDarkBackground?: boolean;
}>`
  align-self: stretch;

  svg {
    color: ${({ theme, $isOnDarkBackground }) =>
      $isOnDarkBackground ? "white" : theme.palette.dream.grayMediumDark};
  }
`;

const NavMobileMenuButton = styled(IconButton)`
  height: 100%;
  padding: 0 1rem 0 0;

  font-size: 1rem;
  border-radius: 0.5rem;

  display: flex;
  align-items: center;
  transition: 100ms linear background-color;
`;

const NavHolster = styled(Box, {
  shouldForwardProp: (prop) => prop !== "$isMobileMenuOpen" && prop !== "$shouldHideSearch",
})<{ $isMobileMenuOpen: boolean; $shouldHideSearch: boolean }>`
  display: flex;

  ${({ theme }) => theme.breakpoints.down(LAYOUT_MOBILE_DESKTOP_RESPONSIVE_BREAKPOINT)} {
    position: fixed;
    // Avoid setting z-index too high: https://mui.com/material-ui/customization/z-index/
    z-index: 999;
    left: 0;
    top: ${({ $shouldHideSearch }) =>
      $shouldHideSearch ? "var(--NavBarHeight)" : "calc(var(--NavBarHeight) * 2)"};
    height: calc(100vh - var(--NavBarMobileHeight));
    width: 100%;
    background: ${({ theme }) => theme.palette.dream.grayLight};

    /* Layout items vertically -- individual styling updates on individual components */
    flex-direction: column;
    padding: 0 1rem;
  }

  ${({ theme }) => theme.breakpoints.up(LAYOUT_MOBILE_DESKTOP_RESPONSIVE_BREAKPOINT)} {
    flex: 1 1;
    height: 100%;
  }
`;

const SearchContainer = styled(Box)`
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;

  ${({ theme }) => theme.breakpoints.down(LAYOUT_MOBILE_DESKTOP_RESPONSIVE_BREAKPOINT)} {
    flex: 1 0 100%;
  }

  ${({ theme }) => theme.breakpoints.up(LAYOUT_MOBILE_DESKTOP_RESPONSIVE_BREAKPOINT)} {
    align-self: stretch;
  }
`;

const UserContainer = styled(Box)`
  display: flex;
  align-items: center;

  ${({ theme }) => theme.breakpoints.down(LAYOUT_MOBILE_DESKTOP_RESPONSIVE_BREAKPOINT)} {
    flex-wrap: wrap;
  }

  ${({ theme }) => theme.breakpoints.up(LAYOUT_MOBILE_DESKTOP_RESPONSIVE_BREAKPOINT)} {
    align-self: stretch;
  }
`;
